
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ResourceFile } from "../types";
import { determineResourceType } from "../utils/fileTypeUtils";

export function useTrainingContent(resourceId: string) {
  const [files, setFiles] = useState<ResourceFile[]>([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [freeResponseQuestions, setFreeResponseQuestions] = useState<any[]>([]);
  const [hasAttemptedFreeResponseFetch, setHasAttemptedFreeResponseFetch] = useState(false);
  const { toast } = useToast();
  
  // Fetch the training resources
  useEffect(() => {
    async function fetchResources() {
      try {
        setLoadingError(null);
        console.log('Fetching resources for:', resourceId);
        const { data: resourceFiles, error } = await supabase
          .from('resource_files')
          .select('*')
          .eq('resource_id', resourceId)
          .order('order_index');

        if (error) throw error;

        console.log('Found resources:', resourceFiles);
        if (resourceFiles) {
          const typedFiles = resourceFiles.map(file => {
            const fileType = file.file_type 
              ? file.file_type
              : determineResourceType(file.file_url, file.file_type);
            
            return {
              ...file,
              resourceType: fileType
            } as ResourceFile;
          });
          
          setFiles(typedFiles);
        }
      } catch (error) {
        console.error('Error fetching resources:', error);
        setLoadingError('Failed to load training resources');
        toast({
          title: "Error loading resources",
          description: "Please try refreshing the page",
          variant: "destructive",
        });
      }
    }

    if (resourceId) {
      fetchResources();
    }
  }, [resourceId, toast]);
  
  // Function to fetch free response questions
  const fetchFreeResponseQuestions = async (fileId: string) => {
    try {
      const { data, error } = await supabase
        .from('free_response_questions')
        .select('*')
        .eq('file_id', fileId)
        .order('order_index');

      if (error) throw error;
      setFreeResponseQuestions(data || []);
    } catch (error) {
      console.error('Error fetching free response questions:', error);
      setFreeResponseQuestions([]);
      toast({
        title: "Error loading questions",
        description: "Please try refreshing the page",
        variant: "destructive",
      });
    }
  };
  
  const handleFileChange = (index: number) => {
    setCurrentFileIndex(index);
    setHasAttemptedFreeResponseFetch(false);
    setLoadingError(null);
  };
  
  return {
    files,
    currentFileIndex,
    currentFile: files[currentFileIndex],
    loadingError,
    setLoadingError,
    freeResponseQuestions,
    hasAttemptedFreeResponseFetch,
    setHasAttemptedFreeResponseFetch,
    fetchFreeResponseQuestions,
    handleFileChange
  };
}
